import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  COLUMN_MAPPING_STATUS_ACTION,
  COLUMN_MAPPING_STATUS,
} from "../../constants/columnMappingStatus";
import { fileUploadActions } from "../../redux/reducers/fileUploadReducer";
import { FileHandler } from "../../screens/fileUploadWizard";
// core imports
import SFCard, { SFCardBody } from "../../../components/card";
import SFSelect from "../../../components/select";
import { SFStack } from "../../../components/grid";
import { SFWarningIcon } from "../../../components/icons";
import getStyled from "../../../hooks/getStyled";
import SFTypography from "../../../components/typography";

const { styled } = getStyled();

const alphaBetArr = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const StyledTable = styled("div")(({ theme }) => ({
  border: `2px solid ${theme.palette.grey200.main}`,
  "& .header": {
    width: "100%",
    height: 54,
    display: "flex",
    "& .file-col-number": {
      backgroundColor: theme.palette.background.default,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "10%",
    },
    "& .file-col-content": {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(0, 2),
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
      width: "40%",
      "& .MuiSelect-icon": {
        top: "calc(50% - 0.8em)",
        right: 0,
      },
    },
    "& .triangle": {
      width: 0,
      height: 0,
      borderStyle: "solid",
      borderWidth: "27px 0 27px 15px",
      borderColor: `transparent transparent transparent ${theme.palette.grey200.main}`,
      "& .inner-triangle": {
        position: "relative",
        top: "-26px",
        left: "-16px",
        width: 0,
        height: 0,
        borderStyle: "solid",
        borderWidth: "26px 0 26px 14px",
        borderColor: `transparent transparent transparent ${theme.palette.background.default}`,
      },
    },
    "& .match-col": {
      width: "50%",
      padding: theme.spacing(0, 2),
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
    },
  },
  "& .body": {
    display: "flex",
    flexDirection: "column",
    "& .file-row": {
      borderTop: `2px solid ${theme.palette.grey200.main}`,
      height: 54,
      display: "flex",
      "& .file-row-number": {
        width: "10%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRight: `2px solid ${theme.palette.grey200.main}`,
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(2),
      },
      "& .file-row-content": {
        width: "90%",
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(2),
      },
    },
  },
}));

const StyledTypography = styled(SFTypography)(({ theme }) => ({
  minHeight: 27,
  fontWeight: 700,
}));

const StyledBodyRowDataTypography = styled(SFTypography)(({ theme }) => ({
  overflow: "hidden",
  whiteSpace: "normal",
  maxWidth: 600,
  display: "-webkit-box",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical"
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledCardWrapper = styled("div", { shouldForwardProp: false })(
  ({ theme, showWarningBorder }) => ({
    border: showWarningBorder
      ? `1px solid ${theme.palette.red300.main}`
      : "none",
    borderRadius: 4,
  })
);

const transformToText = (item, t) => {
  let text = "";
  if (typeof item === "boolean") {
    text = item
      ? t("fileUploadWizard.generic.yes")
      : t("fileUploadWizard.generic.no");
  } else {
    text = item.toString();
  }
  return text;
};

const BodyRowData = ({ value, rowIndex }) => {
  const { t } = useTranslation();
  return (
    <div className="file-row">
      <div className="file-row-number">{rowIndex}</div>
      <div className="file-row-content">
        <StyledBodyRowDataTypography variant="body1bold">
          {typeof value !== "undefined" ? transformToText(value, t) : "-"}
        </StyledBodyRowDataTypography>
      </div>
    </div>
  );
};

const getNoOfNonEmptyColData = (colName, fileData, rows) => {
  let arr = [];
  if (colName) {
    for (let index = 0; index < fileData.length; index++) {
      if (
        colName in fileData[index] &&
        fileData[index][colName].toString().length > 0
      ) {
        arr.push(fileData[index][colName]);
      }
      if (arr.length === rows) {
        break;
      }
    }
  }

  return arr;
};

const ColumnMatchTable = ({ mapping, colIndex, showWarning }) => {
  const dispatch = useDispatch();
  const reduxState = useSelector((state) => state.fileUpload);

  const [colMatchTableState, setColMatchTableState] = useState({
    selected: mapping.src ?? "",
    showWarningBorder:
      showWarning && mapping.status === COLUMN_MAPPING_STATUS.NO_MATCH,
    srcColData: getNoOfNonEmptyColData(mapping.src, FileHandler.fileData, 2),
  });
  useEffect(() => {
    setColMatchTableState((curState) => ({
      ...curState,
      showWarningBorder:
        showWarning && mapping.status === COLUMN_MAPPING_STATUS.NO_MATCH,
    }));
  }, [showWarning]);
  const { t } = useTranslation();

  const getMatchingLabel = () => {
    let label;
    switch (mapping.status) {
    case COLUMN_MAPPING_STATUS.MATCHED:
      label = t("fileUploadWizard.fileMapping.exactMatchLabel");
      break;
    case COLUMN_MAPPING_STATUS.POSSIBLY_MATCHED:
      label = t("fileUploadWizard.fileMapping.possibleMatchLabel");
      break;
    case COLUMN_MAPPING_STATUS.NO_MATCH:
      label = t("fileUploadWizard.fileMapping.noMatchLabel");
      break;
    case COLUMN_MAPPING_STATUS.MANUAL_MATCH:
      label = t("fileUploadWizard.fileMapping.manualMatchLabel");
      break;
    default:
      label = t("fileUploadWizard.fileMapping.noMatchLabel");
      break;
    }
    return label;
  };

  const selectSrcHandler = (option) => {
    const val = option.value;
    setColMatchTableState((curState) => ({
      ...curState,
      selected: val,
      showWarningBorder: false,
      srcColData: getNoOfNonEmptyColData(val, FileHandler.fileData, 2),
    }));
    dispatch(
      fileUploadActions.setColumnMappings({
        src: val,
        dest: mapping.dest,
        fileData: FileHandler.fileData,
        t,
      })
    );
  };

  return (
    <StyledCardWrapper showWarningBorder={colMatchTableState.showWarningBorder}>
      <SFCard>
        <SFCardBody>
          {mapping.required &&
            mapping.action !== COLUMN_MAPPING_STATUS_ACTION.CONFIRMED && (
            <StyledStack alignItems="center" direction="row" spacing={2}>
              <SFWarningIcon size="small" color="red300" />
              <SFTypography variant="body1bold">
                {t("fileUploadWizard.fileMapping.requiredMappingLabel")}
              </SFTypography>
            </StyledStack>
          )}
          <StyledTable>
            <div className="header">
              <SFTypography variant="body1bold" className="file-col-number">
                {alphaBetArr[colIndex]}
              </SFTypography>
              <div className="file-col-content">
                <SFTypography variant="body2bold">
                  {getMatchingLabel()}
                </SFTypography>
                {mapping.action === COLUMN_MAPPING_STATUS_ACTION.EDIT && (
                  <SFSelect
                    variant="transparent"
                    placeholder={t(
                      "fileUploadWizard.fileMapping.selectPlaceholder"
                    )}
                    name="match-select"
                    hasSearch={false}
                    onChangeFilter={selectSrcHandler}
                    values={reduxState.srcColNames}
                    selectedValue={colMatchTableState.selected}
                  />
                )}
                {mapping.action !== COLUMN_MAPPING_STATUS_ACTION.EDIT && (
                  <StyledTypography variant="body1bold">
                    {colMatchTableState.selected}
                  </StyledTypography>
                )}
              </div>
              <div className="triangle">
                <div className="inner-triangle"></div>
              </div>
              <div className="match-col">
                <SFTypography variant="body2bold">
                  {mapping.required
                    ? t(
                      "fileUploadWizard.fileMapping.requiredColumnSystemStandardLabel"
                    )
                    : t(
                      "fileUploadWizard.fileMapping.optionalColumnSystemStandardLabel"
                    )}
                </SFTypography>
                <StyledTypography variant="body1bold">
                  {mapping.dest}
                </StyledTypography>
              </div>
            </div>
            {mapping.action === COLUMN_MAPPING_STATUS_ACTION.EDIT && (
              <div className="body">
                <BodyRowData
                  value={colMatchTableState.srcColData[0]}
                  rowIndex={2}
                />
                <BodyRowData
                  value={colMatchTableState.srcColData[1]}
                  rowIndex={3}
                />
              </div>
            )}
          </StyledTable>
        </SFCardBody>
      </SFCard>
    </StyledCardWrapper>
  );
};

ColumnMatchTable.propTypes = {
  mapping: PropTypes.object.isRequired,
  colIndex: PropTypes.number.isRequired,
  showWarning: PropTypes.bool,
};

export default ColumnMatchTable;
